import React from "react";
import { tLang } from "@/contexts/PortalLanguageContext";

const NAVY = "#1A1A2E";

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
}

export default class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[ErrorBoundary] Uncaught render error:", error, info.componentStack);
  }

  /** Read stored locale from localStorage — React context is unavailable here. */
  private getLang(): string {
    try {
      const saved = localStorage.getItem("portalLanguage");
      if (saved === "fr" || saved === "es") return saved;
    } catch {
      /* localStorage may be blocked — fall back to English */
    }
    return "en";
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    const lang = this.getLang();
    const t = (key: string, fallback: string) =>
      tLang(lang, `error.boundary.${key}`, fallback);

    const { error } = this.state;

    return (
      <div
        className="min-h-screen flex items-center justify-center p-6"
        style={{ backgroundColor: NAVY }}
      >
        <div className="w-full max-w-lg text-center space-y-6">
          {/* Logo */}
          <img
            src="/fyrris-logo.png"
            alt="Fyrris logo"
            className="h-10 mx-auto"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />

          {/* Heading */}
          <h1 className="text-3xl font-bold text-white">
            {t("heading", "Something went wrong.")}
          </h1>

          {/* Subtext */}
          <p className="text-base leading-relaxed text-gray-300">
            {t("subtext", "We've been notified and are looking into it.")}
          </p>

          {/* Action buttons */}
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={this.handleReload}
              className="rounded-lg bg-[#FF6B35] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B35] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1A1A2E]"
            >
              {t("reload", "Reload Page")}
            </button>
            <button
              onClick={this.handleGoHome}
              className="rounded-lg border border-gray-600 px-5 py-2.5 text-sm font-semibold text-gray-300 transition hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#1A1A2E]"
            >
              {t("goHome", "Go Home")}
            </button>
          </div>

          {/* Dev-only: error message + stack trace */}
          {import.meta.env.DEV && (
            <details className="mt-4 rounded-xl border border-gray-700 bg-gray-900 p-4 text-left">
              <summary className="cursor-pointer select-none text-xs font-medium text-gray-400">
                Error details (dev only)
              </summary>
              <div className="mt-3 space-y-2">
                <p className="break-all font-mono text-sm text-red-400">
                  {error.message}
                </p>
                {error.stack && (
                  <pre className="overflow-x-auto whitespace-pre-wrap text-xs leading-relaxed text-gray-500">
                    {error.stack}
                  </pre>
                )}
              </div>
            </details>
          )}
        </div>
      </div>
    );
  }
}
